import { ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  CirclePlusIcon,
  FilePlusIcon,
  FolderOpenIcon,
  MaximizeIcon,
  MinusIcon,
  SpinnerIcon,
  WarningIcon
} from "@/assets/icons";
import ButtonMenu from "@/components/ui/buttonMenu";
import CollapseView from "@/components/ui/collapseView";
import { Divider } from "@/components/ui/Divider";
import Tooltip from "@/components/ui/tooltip";
import { useAnalysisConfigHydration } from "@/hooks/spatialAnalysis/useAnalysisConfigHydration";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { setAnalysisConfigMinimized } from "@/store/slice/uiSlice";
import { EOutputAnalysisType } from "@/types/spatial";

import { Button } from "equator-design-system";
import { twMerge } from "tailwind-merge";

import { GlobalFilter } from "../../globalFilter";
import DasimetrySection from "../dasimetrySection";
import SpatialHeader from "../header";
import ProjectForm from "../projectForm";
import SpatialAreaInput from "../spatialAreaInput";
import SpatialOutputAnalysis from "../spatialOutputAnalysis";
import AnalysisConfigDatasetList from "./datasetList";

const TEST_ID_PREFIX = "analysis-config-view-only";

const noop = () => {};

const ConfigSkeleton = () => (
  <div
    className="flex flex-col justify-center gap-4 px-4 mt-4 h-[calc(100vh-54px)]"
    data-testid={`${TEST_ID_PREFIX}-skeleton`}
  >
    <div className="flex flex-col items-center gap-2 mt-2">
      <SpinnerIcon className="spin-animation" width={24} height={24} />
      <span className="text-txtPlaceholder text-xs">
        Loading configuration...
      </span>
    </div>
  </div>
);

const ConfigError = () => (
  <div
    className="flex flex-col justify-center items-center gap-2 px-4 mt-6 text-center h-[calc(100vh-54px)]"
    data-testid={`${TEST_ID_PREFIX}-error`}
  >
    <WarningIcon className="fill-red-400 size-6" />
    <span className="text-txtHeading font-sans text-sm font-semibold">
      Configuration unavailable
    </span>
    <p className="text-txtBody font-sans text-xs">
      We couldn&apos;t load the configuration for this analysis. Reopen the
      analysis from the job list to try again.
    </p>
  </div>
);

const ReadOnlyFields = ({ children }: { children: ReactNode }) => (
  <div
    ref={(node) => node?.setAttribute("inert", "")}
    className="pointer-events-none opacity-60"
  >
    {children}
  </div>
);

/**
 * Read-only Analysis Config panel, shown beside the Analysis Result panel.
 * Feature-flagged by `isAnalysisConfigViewOnlyEnabled`.
 *
 * Separate from `SpatialAnalysisPopup` because that popup reads the live dataset collections.
 */
const AnalysisConfigViewOnly = () => {
  const { isGlobalFilterEnabled, isDasimetryEnabled } = useAppFeatures();
  const dispatch = useDispatch();

  const spatial = useSelector((state: RootState) => state.spatial);
  const history = useSelector((state: RootState) => state.dataset.history);
  const isMinimized = useSelector(
    (state: RootState) => state.ui.isAnalysisConfigMinimized
  );

  const hydration = useAnalysisConfigHydration();
  const isHydrating = hydration === "loading";
  const hasHydrationError = hydration === "error";

  const hasDatasets = history.visualizationOrder.length > 0;

  const headerActions = (
    <div className="flex gap-3.5">
      <Tooltip content="Reset Analysis" position="bottom-center">
        <button
          type="button"
          data-testid={`${TEST_ID_PREFIX}-btn-reset`}
          className="bg-transparent border-0 p-0 cursor-not-allowed fill-light-gray"
          disabled
          aria-disabled
          aria-label="Reset Analysis"
        >
          <FilePlusIcon />
        </button>
      </Tooltip>
      <Tooltip content="Open Project" position="bottom-center">
        <button
          type="button"
          data-testid={`${TEST_ID_PREFIX}-btn-open-project`}
          className="bg-transparent border-0 p-0 cursor-not-allowed fill-light-gray"
          disabled
          aria-disabled
          aria-label="Open Project"
        >
          <FolderOpenIcon className="text-light-gray" />
        </button>
      </Tooltip>
      <Tooltip
        content={isMinimized ? "Expand Analysis" : "Minimize Analysis"}
        position="bottom-center"
      >
        <button
          type="button"
          data-testid={`${TEST_ID_PREFIX}-btn-minimize`}
          className="bg-transparent border-0 p-0 cursor-pointer"
          onClick={() => dispatch(setAnalysisConfigMinimized(!isMinimized))}
          aria-expanded={!isMinimized}
          aria-label={isMinimized ? "Expand Analysis" : "Minimize Analysis"}
        >
          {isMinimized ? (
            <MaximizeIcon className="fill-dim-gray size-6" />
          ) : (
            <MinusIcon className="fill-dim-gray size-6" />
          )}
        </button>
      </Tooltip>
    </div>
  );

  return (
    <div
      className={twMerge(
        "absolute top-[30px] left-[16px] z-[9] border-gainsboro bg-white rounded-lg w-[338px] overflow-hidden",
        "flex flex-col",
        /* Collapsed to a header-only bar. The Analysis Result panel reads the
           same `isAnalysisConfigMinimized` flag and stacks itself directly
           below this bar, so 56px is part of that panel's offset too. */
        isMinimized ? "h-[56px]" : "max-h-[calc(100vh-54px)]",
        !isMinimized &&
          (isHydrating || hasHydrationError || !hasDatasets) &&
          "min-h-[224px]"
      )}
      data-testid={TEST_ID_PREFIX}
    >
      <SpatialHeader
        title="Analysis Config"
        onClose={noop}
        withCloseButton={false}
      >
        {headerActions}
      </SpatialHeader>

      {!isMinimized && (
        <>
          {/* Content */}
          <div className="flex flex-col mt-3 custom-scrollbar overflow-y-auto pb-[64px]">
            {isHydrating && <ConfigSkeleton />}
            {hasHydrationError && <ConfigError />}
            {!isHydrating && !hasHydrationError && (
              <>
                {spatial.currentConfigId && (
                  <ReadOnlyFields>
                    <ProjectForm
                      initialData={{
                        id: spatial.currentConfigId,
                        projectName: spatial.title ?? "",
                        projectDescription: spatial.description ?? ""
                      }}
                      onUpdate={noop}
                    />
                  </ReadOnlyFields>
                )}

                {isGlobalFilterEnabled && (
                  <>
                    <ReadOnlyFields>
                      <GlobalFilter onClearFilter={noop} onOpenFilter={noop} />
                    </ReadOnlyFields>
                    <Divider className="bg-white-smoke border-none w-[100%] min-h-[8px] my-4 [&>div]:border-0" />
                  </>
                )}

                {/* Data selection */}
                <div className="flex flex-col px-4">
                  <span className="font-sans font-semibold text-txtHeading text-sm">
                    Data Selection
                  </span>
                  <p className=" text-txtBody font-sans text-[10px]">
                    Select your own data or explore our dataset library.
                  </p>

                  <AnalysisConfigDatasetList />

                  <div className="flex flex-row items-center gap-2 mt-3 ">
                    <Button
                      variant="secondary"
                      disabled
                      className="h-8 btn-secondary text-sm !font-bold flex-grow flex-shrink-0 disabled:cursor-not-allowed disabled:text-neutral-200 disabled:hover:bg-transparent"
                      data-testid={`${TEST_ID_PREFIX}-add-dataset-button`}
                    >
                      <CirclePlusIcon className="fill-gray-300" />
                      <span>Add Dataset</span>
                    </Button>
                  </div>
                </div>

                <Divider className="bg-white-smoke border-none w-[100%] min-h-[8px] mt-4 [&>div]:border-0" />
                {/* Open by default (unlike the editable panel): these settings are
            what this panel exists to show. Collapsing changes no value. */}
                <CollapseView title="Spatial Settings" expanded>
                  <ReadOnlyFields>
                    <SpatialAreaInput
                      onOpenCatchmentPopup={noop}
                      onOpenAreaAdminPopup={noop}
                      onOpenPolygonPopup={noop}
                    />
                    <SpatialOutputAnalysis />
                    <div className="flex flex-col gap-4 px-4 my-2">
                      <Button
                        variant="secondary"
                        disabled
                        className="h-[32px] w-full btn-secondary text-sm !font-bold"
                        data-testid={`${TEST_ID_PREFIX}-set-data-weight-button`}
                      >
                        {spatial.spatialConfig.outputType ===
                        EOutputAnalysisType.GRID ? (
                          <span>Setting Data Weight</span>
                        ) : (
                          <span>Set Data Weight</span>
                        )}
                      </Button>
                    </div>
                    {isDasimetryEnabled && <DasimetrySection />}
                  </ReadOnlyFields>
                </CollapseView>
                <Divider className="bg-white-smoke w-[100%] min-h-[8px] [&>div]:border-0" />
              </>
            )}
          </div>

          {/* Bottom button config */}
          <div className="flex px-4 gap-2 absolute bottom-0 bg-white w-full py-3 rounded-t-none rounded-lg">
            <ButtonMenu
              variant="secondary"
              title="Save as"
              className="w-1/2 !font-bold disabled:cursor-not-allowed disabled:hover:bg-none"
              menu={[]}
              disabled
              data-testid={`${TEST_ID_PREFIX}-save-as-button`}
            />
            <Button
              className="h-[40px] w-1/2 btn-primary text-sm !font-bold disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Generate Results"
              disabled
              data-testid={`${TEST_ID_PREFIX}-generate-results-button`}
            >
              <span>Generate Results</span>
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default AnalysisConfigViewOnly;
